import {
  formatInZone,
  hoursApart,
  isDomesticCanadian,
  specialtyLabel,
  type Appointment,
  type AppointmentStatus,
  type Specialty,
} from "@so/shared";
import { Badge } from "./ui.js";

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  requested: "Awaiting specialist",
  confirmed: "Confirmed",
  completed: "Consultation held",
  reported: "Opinion issued",
  cancelled: "Cancelled",
};

const STATUS_TONE: Record<AppointmentStatus, "neutral" | "accent" | "warn" | "caution"> = {
  requested: "caution",
  confirmed: "accent",
  completed: "neutral",
  reported: "accent",
  cancelled: "warn",
};

export function StatusBadge({ status }: { readonly status: AppointmentStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>;
}

export function statusLabel(status: AppointmentStatus): string {
  return STATUS_LABEL[status];
}

/**
 * An appointment time, always with its zone.
 *
 * A bare "14:00" is how somebody misses a consultation by nine and a half
 * hours, so the zone is never optional here. When the two participants are far
 * enough apart to matter, the other side's local time is shown too - a
 * coordinator should not have to do that arithmetic in their head.
 */
export function AppointmentTime({
  appointment,
  viewerTimezone,
  showCounterpart = false,
}: {
  readonly appointment: Appointment;
  readonly viewerTimezone: string;
  readonly showCounterpart?: boolean;
}) {
  const local = formatInZone(appointment.starts_at, viewerTimezone);

  const otherZone =
    viewerTimezone === appointment.patient_tz ? appointment.clinician_tz : appointment.patient_tz;

  const gap = Math.abs(
    hoursApart(appointment.starts_at, appointment.patient_tz, appointment.clinician_tz),
  );

  const worthShowing = showCounterpart && gap >= 1 && otherZone !== viewerTimezone;

  return (
    <div>
      <p className="text-[15px] font-medium text-ink">
        {local.date}
        <span className="text-ink-faint mx-1.5">·</span>
        {local.time}
        <span className="text-ink-faint ml-1.5 font-mono text-[12.5px]">
          {local.zoneAbbreviation}
        </span>
      </p>
      {worthShowing && (
        <p className="text-ink-faint mt-0.5 text-[13px]">
          {formatInZone(appointment.starts_at, otherZone).time} for the other participant
          <span className="mx-1">·</span>
          {gap % 1 === 0 ? gap : gap.toFixed(1)} hours apart
        </p>
      )}
    </div>
  );
}

export function SpecialtyBadge({ specialty }: { readonly specialty: string }) {
  return <Badge>{specialtyLabel(specialty as Specialty)}</Badge>;
}

/**
 * Where this consultation's video is hosted.
 *
 * Surfaced rather than buried in a config file: for a Canadian patient the
 * media never leaves Canada, and being able to point at that on screen is the
 * difference between claiming it and showing it.
 */
export function ResidencyNote({ region }: { readonly region: string }) {
  const domestic = isDomesticCanadian(region);

  return (
    <p className="text-ink-faint text-[12.5px] leading-relaxed">
      <span className="font-mono">{region}</span>
      {domestic
        ? " — video and audio for this consultation stay within Canada."
        : " — video and audio are hosted in the region nearest the patient."}
    </p>
  );
}
